import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Send, Inbox, AlertCircle, ChevronUp, ChevronDown, Filter } from 'lucide-react';
import { apiService } from '../services/api';
import useMeetingsInbox from '../hooks/useMeetingsInbox';
import NavigationBar from './NavigationBar';
import EventNavigation from './EventNavigation';
import LoadingSpinner from './LoadingSpinner';
import MeetingDetailsDrawer from './MeetingDetailsDrawer';
import './EventHome.css';
import './EventMeetings.css';

const EventMeetings = () => {
    const { eventNameId, tab } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedMeetingId, setSelectedMeetingId] = useState(null);
    const [selectedCompanyName, setSelectedCompanyName] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'desc' });

    // Entity type filter state (for Received tab)
    const [selectedEntityTypes, setSelectedEntityTypes] = useState(new Set());
    const [filterInitialized, setFilterInitialized] = useState(false);
    const [showTypeFilter, setShowTypeFilter] = useState(false);
    const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });
    const filterBtnRef = useRef(null);

    // Determine active tab from URL parameter, default to 'confirmed'
    const validTabs = ['confirmed', 'received', 'sent'];
    const activeTab = validTabs.includes(tab) ? tab : 'confirmed';

    // Handle tab change - update URL
    const handleTabChange = (newTab) => {
        navigate(`/event/${eventNameId}/meetings/${newTab}`, { replace: true });
    };

    // Use the reusable meetings inbox hook - pass eventId to filter by confirmed contacts
    const {
        sent,
        received,
        confirmed,
        loading: meetingsLoading,
        loaded: meetingsLoaded,
        error: meetingsError,
        pendingActionCount,
        refresh: refreshMeetings,
        userEntityType,
        availableEntityTypes
    } = useMeetingsInbox(event?.ic_event_name, { autoFetch: false, eventId: event?.EventId, useSqlDirect: true, icEventId: event?.ic_event_id });
    //} = useMeetingsInbox(event?.ic_event_name, { autoFetch: false, eventId: event?.EventId });


    // Initialize entity type filter when data loads
    // If user is a Philanthropist, exclude Philanthropist from default selection
    useEffect(() => {
        if (availableEntityTypes?.length > 0 && !filterInitialized) {
            let typesToSelect = availableEntityTypes;
            
            // If user is a Philanthropist, exclude Philanthropist from default filter
            if (userEntityType === 'Philanthropist') {
                typesToSelect = availableEntityTypes.filter(t => t !== 'Philanthropist');
            }
            
            setSelectedEntityTypes(new Set(typesToSelect));
            setFilterInitialized(true);
        }
    }, [availableEntityTypes, filterInitialized, userEntityType]);

    // Handle entity type filter toggle
    const handleEntityTypeToggle = (entityType) => {
        setSelectedEntityTypes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(entityType)) {
                newSet.delete(entityType);
            } else {
                newSet.add(entityType);
            }
            return newSet;
        });
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            if (showTypeFilter) {
                setShowTypeFilter(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showTypeFilter]);

    useEffect(() => {
        if (eventNameId) {
            localStorage.setItem('last_event', eventNameId);
            fetchEventData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventNameId]);

    // Fetch meetings when event is loaded
    useEffect(() => {
        if (event?.ic_event_name) {
            refreshMeetings();
        }
    }, [event?.ic_event_name, refreshMeetings]);

    const fetchEventData = async () => {
        try {
            setLoading(true);
            const eventResponse = await apiService.getEventByNameId(eventNameId);
            setEvent(eventResponse.data);
        } catch (err) {
            console.error('Error fetching event:', err);
            setError('Failed to load event');
        } finally {
            setLoading(false);
        }
    };

    // Get company name directly from the meeting.company field
    const getCompanyName = (meeting) => {
        return meeting.company || 'Unknown Company';
    };

    // Get meetings for active tab (with entity type filter for received)
    const activeMeetings = useMemo(() => {
        let meetings;
        switch (activeTab) {
            case 'confirmed':
                meetings = confirmed;
                break;
            case 'received':
                meetings = received;
                // Apply entity type filter on received tab
                if (selectedEntityTypes.size > 0) {
                    meetings = meetings.filter(meeting =>
                        meeting.counterpartyEntityType && selectedEntityTypes.has(meeting.counterpartyEntityType)
                    );
                }
                break;
            case 'sent':
                meetings = sent;
                break;
            default:
                meetings = [];
        }
        return meetings;
    }, [activeTab, confirmed, received, sent, selectedEntityTypes]);

    // Sort meetings
    const sortedMeetings = useMemo(() => {
        if (!activeMeetings.length) return [];

        const sorted = [...activeMeetings].sort((a, b) => {
            let aValue, bValue;

            switch (sortConfig.key) {
                case 'company':
                    aValue = getCompanyName(a).toLowerCase();
                    bValue = getCompanyName(b).toLowerCase();
                    break;
                case 'status':
                    aValue = a.status || '';
                    bValue = b.status || '';
                    break;
                case 'updatedAt':
                    aValue = new Date(a.updatedAt || a.createdAt || 0);
                    bValue = new Date(b.updatedAt || b.createdAt || 0);
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return sorted;
    }, [activeMeetings, sortConfig]);

    // Format time for display (backend already converts to event timezone)
    const formatUpdatedAt = (dateString) => {
        if (!dateString) return '';
        return dateString; // Backend now returns formatted time with timezone
    };

    // Format start time for confirmed meetings using the event's timezone
    const formatStartTime = (dateString, timeZone) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const opts = {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short'
        };
        if (timeZone) {
            opts.timeZone = timeZone;
        }
        return date.toLocaleString('en-US', opts);
    };

    // Get date/time display for confirmed meetings
    const getDateTimeDisplay = (meeting) => {
        if (meeting.startTime) {
            return formatStartTime(meeting.startTime, meeting.timeZone);
        }
        return meeting.dateTimeMessage || 'TBD';
    };

    // Handle sort
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Render sort icon
    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <ChevronUp size={14} className="sort-icon inactive" />;
        }
        return sortConfig.direction === 'asc'
            ? <ChevronUp size={14} className="sort-icon active" />
            : <ChevronDown size={14} className="sort-icon active" />;
    };

    // State for selected meeting entity info
    const [selectedMeetingEntityType, setSelectedMeetingEntityType] = useState(null);
    const [selectedMeetingEventCompanyId, setSelectedMeetingEventCompanyId] = useState(null);

    // Get counterparty event company ID from meeting based on active tab
    const getCounterpartyEventCompanyIdFromMeeting = (meeting) => {
        // For sent tab: user is organizer, counterparty is participant
        // For received tab: user is receiver, counterparty is organizer
        // For confirmed tab: check organizer vs company name to determine
        if (activeTab === 'sent') {
            return meeting.participantEventCompanyId;
        } else if (activeTab === 'received') {
            return meeting.organizerEventCompanyId;
        } else {
            // For confirmed, the counterparty company name matches the company field
            // Check if organizer's company matches the counterparty company name
            const counterpartyCompanyName = meeting.company || meeting.meetingWithCompany;
            const organizerCompanyName = meeting.organizer?.company?.name;
            if (organizerCompanyName === counterpartyCompanyName) {
                return meeting.organizerEventCompanyId;
            } else {
                return meeting.participantEventCompanyId;
            }
        }
    };

    // Handle row click
    const handleRowClick = (meeting) => {
        setSelectedMeetingId(meeting.meetingId);
        setSelectedCompanyName(getCompanyName(meeting));
        setSelectedMeetingEntityType(meeting.counterpartyEntityType || null);
        setSelectedMeetingEventCompanyId(getCounterpartyEventCompanyIdFromMeeting(meeting));
        setIsDrawerOpen(true);
    };

    // Handle drawer close
    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setSelectedMeetingId(null);
        setSelectedCompanyName(null);
    };

    // Handle meeting cancelled
    const handleMeetingCancelled = (meetingId) => {
        refreshMeetings();
    };

    // Render meeting table
    const renderMeetingsTable = () => {
        return (
            <div className="meetings-table-container">
                <table className="meetings-table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('company')}>
                                <span>Company</span>
                                {renderSortIcon('company')}
                            </th>
                            <th className="type-header-cell">
                                {activeTab === 'received' && availableEntityTypes?.length > 0 ? (
                                    <div className="type-filter-header">
                                        <button
                                            ref={filterBtnRef}
                                            className={`type-filter-btn ${showTypeFilter ? 'active' : ''} ${selectedEntityTypes.size < availableEntityTypes.length ? 'has-filter' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!showTypeFilter && filterBtnRef.current) {
                                                    const rect = filterBtnRef.current.getBoundingClientRect();
                                                    setFilterPosition({
                                                        top: rect.bottom + 4,
                                                        left: rect.left
                                                    });
                                                }
                                                setShowTypeFilter(!showTypeFilter);
                                            }}
                                        >
                                            <span>Attendee Type</span>
                                            <Filter size={14} />
                                            {selectedEntityTypes.size < availableEntityTypes.length && (
                                                <span className="filter-count">
                                                    {selectedEntityTypes.size}/{availableEntityTypes.length}
                                                </span>
                                            )}
                                        </button>
                                        {showTypeFilter && (
                                            <div
                                                className="type-filter-dropdown"
                                                style={{ top: filterPosition.top, left: filterPosition.left }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div className="type-filter-dropdown-header">
                                                    Filter by Type
                                                </div>
                                                {availableEntityTypes.map(entityType => (
                                                    <label key={entityType} className="type-filter-option">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedEntityTypes.has(entityType)}
                                                            onChange={() => handleEntityTypeToggle(entityType)}
                                                        />
                                                        <span className={entityType === userEntityType ? 'own-type' : ''}>
                                                            {entityType}
                                                            {entityType === userEntityType && ' (You)'}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <span>Type</span>
                                )}
                            </th>
                            <th>
                                <span>Attendees</span>
                            </th>
                            {activeTab === 'confirmed' && (
                                <th>
                                    <span>Date/Time</span>
                                </th>
                            )}
                            {activeTab === 'confirmed' && (
                                <th>
                                    <span>Location</span>
                                </th>
                            )}
                            <th onClick={() => handleSort('status')}>
                                <span>Status</span>
                                {renderSortIcon('status')}
                            </th>
                            <th onClick={() => handleSort('updatedAt')}>
                                <span>Last updated at</span>
                                {renderSortIcon('updatedAt')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedMeetings.length === 0 && activeTab === 'received' && received.length > 0 ? (
                            <tr>
                                <td colSpan="5" className="no-results-cell">
                                    <div className="no-filter-results">
                                        <Inbox size={32} />
                                        <p>No meetings match your filter.</p>
                                        <button
                                            className="reset-filter-btn"
                                            onClick={() => setSelectedEntityTypes(new Set(availableEntityTypes))}
                                        >
                                            Reset Filters
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ) : sortedMeetings.map(meeting => {
                            const isCancelled = meeting.statusId === 3 || meeting.status === 'Cancelled';
                            const companyName = getCompanyName(meeting);
                            const attendees = meeting.attendees || [];

                            return (
                                <tr
                                    key={meeting.meetingId}
                                    className={`meeting-row ${isCancelled ? 'cancelled' : ''}`}
                                    onClick={() => handleRowClick(meeting)}
                                >
                                    <td className="company-cell">
                                        <span className={isCancelled ? 'cancelled-text' : ''}>
                                            {companyName}
                                        </span>
                                    </td>
                                    <td className="entity-type-cell">
                                        {meeting.counterpartyEntityType && (
                                            <span className={`entity-type-badge entity-type-${meeting.counterpartyEntityType?.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                                                {meeting.counterpartyEntityType}
                                            </span>
                                        )}
                                    </td>
                                    <td className="attendees-cell">
                                        <div className="attendees-avatars-table">
                                            {attendees.slice(0, 4).map((attendee, idx) => (
                                                <div
                                                    key={attendee.contactId || idx}
                                                    className="attendee-avatar-table"
                                                    title={`${attendee.firstName} ${attendee.lastName}`}
                                                    style={{ zIndex: 4 - idx }}
                                                >
                                                    {attendee.imageURL ? (
                                                        <img src={attendee.imageURL} alt={attendee.firstName} />
                                                    ) : (
                                                        <span>{attendee.firstName?.[0]}{attendee.lastName?.[0]}</span>
                                                    )}
                                                </div>
                                            ))}
                                            {attendees.length > 4 && (
                                                <div className="attendee-avatar-table more">
                                                    +{attendees.length - 4}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {activeTab === 'confirmed' && (
                                        <td className="datetime-cell">
                                            {getDateTimeDisplay(meeting)}
                                        </td>
                                    )}
                                    {activeTab === 'confirmed' && (
                                        <td className="location-cell">
                                            {(() => {
                                                const loc = meeting.location || meeting.locationName || '—';
                                                return loc === 'Sponsor Lounge' ? 'F4C - Rotating Booth' : loc;
                                            })()}
                                        </td>
                                    )}
                                    <td className="status-cell">
                                        <span className={`status-pill status-${meeting.status?.toLowerCase()}`}>
                                            {meeting.status}
                                        </span>
                                    </td>
                                    <td className="updated-cell">
                                        {formatUpdatedAt(meeting.updatedAt || meeting.createdAt)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    if (loading) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <LoadingSpinner message="Loading meetings..." />
                    </div>
                </div>
            </>
        );
    }

    if (error || !event) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <div className="error-message">
                            <h2>Error</h2>
                            <p>{error || 'Event not found'}</p>
                            <button onClick={() => navigate('/login')} className="btn btn-primary">
                                Return to Login
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <NavigationBar />
            <div className="event-layout">
                <EventNavigation eventNameId={eventNameId} />
                <div className="event-home-container with-sidebar">
                    <div className="meetings-page">
                        <div className="meetings-header">
                            <h1>Meetings</h1>
                            <p className="meetings-subtitle">
                                Manage your meeting requests for {event.Name}
                            </p>
                        </div>

                        {/* Tabs */}
                        <div className="meetings-tabs">
                            <button
                                className={`meetings-tab ${activeTab === 'confirmed' ? 'active' : ''}`}
                                onClick={() => handleTabChange('confirmed')}
                            >
                                <CheckCircle size={18} />
                                <span>Confirmed</span>
                            </button>
                            <button
                                className={`meetings-tab ${activeTab === 'received' ? 'active' : ''}`}
                                onClick={() => handleTabChange('received')}
                            >
                                <Inbox size={18} />
                                <span>Received</span>
                                {pendingActionCount > 0 && (
                                    <span className="tab-action-badge">{pendingActionCount}</span>
                                )}
                            </button>
                            <button
                                className={`meetings-tab ${activeTab === 'sent' ? 'active' : ''}`}
                                onClick={() => handleTabChange('sent')}
                            >
                                <Send size={18} />
                                <span>Sent</span>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="meetings-content">
                            {meetingsLoading && !meetingsLoaded ? (
                                <div className="meetings-loading">
                                    <LoadingSpinner message="Loading meetings..." />
                                </div>
                            ) : meetingsError && activeMeetings.length === 0 ? (
                                <div className="meetings-error">
                                    <AlertCircle size={48} />
                                    <h3>Unable to Load Meetings</h3>
                                    <p>{meetingsError}</p>
                                    <button onClick={refreshMeetings} className="retry-btn">
                                        Try Again
                                    </button>
                                </div>
                            ) : activeMeetings.length === 0 && !(activeTab === 'received' && availableEntityTypes?.length > 0 && received.length > 0) ? (
                                <div className="meetings-empty">
                                    {activeTab === 'confirmed' && (
                                        <>
                                            <CheckCircle size={48} />
                                            <h3>No Confirmed Meetings</h3>
                                        </>
                                    )}
                                    {activeTab === 'received' && (
                                        <>
                                            <Inbox size={48} />
                                            <h3>No Received Requests</h3>
                                        </>
                                    )}
                                    {activeTab === 'sent' && (
                                        <>
                                            <Send size={48} />
                                            <h3>No Sent Requests</h3>
                                        </>
                                    )}
                                </div>
                            ) : (
                                renderMeetingsTable()
                            )}
                        </div>

                        {/* Refresh indicator */}
                        {meetingsLoading && meetingsLoaded && (
                            <div className="meetings-refreshing">
                                <span className="refresh-spinner"></span>
                                Refreshing...
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Meeting Details Drawer */}
            <MeetingDetailsDrawer
                meetingId={selectedMeetingId}
                isOpen={isDrawerOpen}
                onClose={handleDrawerClose}
                onMeetingCancelled={handleMeetingCancelled}
                activeTab={activeTab}
                counterpartyCompanyName={selectedCompanyName}
                counterpartyEntityType={selectedMeetingEntityType}
                counterpartyEventCompanyId={selectedMeetingEventCompanyId}
            />
        </>
    );
};

export default EventMeetings;

